type StorageType = 'session' | 'local';

export interface PersistConfig {
  key: string;
  version: number;
  storage?: StorageType;
}

interface PersistEnvelope<TState> {
  version: number;
  timestamp: number;
  data: TState;
}

function getStorage(type: StorageType = 'session'): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return type === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function loadPersistedState<TState>(
  config: PersistConfig,
  validate: (value: unknown) => value is TState,
): TState | null {
  const storage = getStorage(config.storage);
  if (!storage) return null;
  const raw = storage.getItem(config.key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PersistEnvelope<unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== config.version) return null;
    if (!validate(parsed.data)) return null;
    return parsed.data;
  } catch {
    storage.removeItem(config.key);
    return null;
  }
}

export function savePersistedState<TState>(
  config: PersistConfig,
  data: TState,
): void {
  const storage = getStorage(config.storage);
  if (!storage) return;
  const envelope: PersistEnvelope<TState> = {
    version: config.version,
    timestamp: Date.now(),
    data,
  };
  storage.setItem(config.key, JSON.stringify(envelope));
}

export function clearPersistedState(config: PersistConfig): void {
  const storage = getStorage(config.storage);
  if (!storage) return;
  storage.removeItem(config.key);
}
